import { ChevronLeft, ChevronRight } from 'lucide-react';

import type { PageResultT } from '@lotushealth/lotushealth_js_sdk/schemas';

import { Button } from '@/shared/ui/shadcn/button';

export interface WorklistPaginationProps {
  readonly pageNumber: number;
  readonly pageResult: PageResultT | null | undefined;
  readonly onPrevious: () => void;
  readonly onNext: () => void;
  readonly isFetching?: boolean;
}

export function WorklistPagination({
  pageNumber,
  pageResult,
  onPrevious,
  onNext,
  isFetching = false,
}: WorklistPaginationProps) {
  const totalPages = pageResult?.TotalPages ?? null;
  const totalCount = pageResult?.TotalCount ?? null;
  const canGoBack = pageResult?.HasPrevious ?? false;
  const canGoForward = pageResult?.HasNext ?? false;

  const locationLabel =
    totalPages !== null && totalPages > 0
      ? `Page ${pageNumber} of ${totalPages}`
      : `Page ${pageNumber}`;

  return (
    <div className="flex items-center justify-between gap-3 text-sm">
      <div className="text-muted-foreground">
        {locationLabel}
        {totalCount !== null && totalCount >= 0 ? (
          <span className="ml-2 opacity-80">({totalCount} total)</span>
        ) : null}
      </div>

      <div className="flex items-center gap-2">
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={!canGoBack || isFetching || pageNumber <= 1}
          onClick={onPrevious}
          aria-label="Previous page"
        >
          <ChevronLeft className="mr-1 size-4" aria-hidden="true" />
          Previous
        </Button>
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={!canGoForward || isFetching}
          onClick={onNext}
          aria-label="Next page"
        >
          Next
          <ChevronRight className="ml-1 size-4" aria-hidden="true" />
        </Button>
      </div>
    </div>
  );
}
